import { ErrorContext, EnhancedErrorLogger } from './enhanced-logger';
import { Breadcrumb, JourneyEvent, classifyError } from './error-types';

export class GlobalErrorTracker {
  private static instance: GlobalErrorTracker;
  private breadcrumbs: Breadcrumb[] = [];
  private journeyEvents: JourneyEvent[] = [];
  private maxBreadcrumbs = 50;
  private maxJourneyEvents = 100;
  private context: Partial<ErrorContext> = {};
  private errorLogger: EnhancedErrorLogger;
  private originalConsoleError: typeof console.error;
  private originalConsoleWarn: typeof console.warn;
  private isInitialized = false;
  
  private constructor() {
    this.errorLogger = EnhancedErrorLogger.getInstance();
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;
  }
  
  static getInstance(): GlobalErrorTracker {
    if (!GlobalErrorTracker.instance) {
      GlobalErrorTracker.instance = new GlobalErrorTracker();
    }
    return GlobalErrorTracker.instance;
  }
  
  initialize() {
    if (this.isInitialized) {
      return;
    }
    
    this.isInitialized = true;
    
    // Capture unhandled errors
    window.addEventListener('error', this.handleError.bind(this));
    
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleRejection.bind(this));
    
    // Wrap console methods
    this.wrapConsole();
    
    // Initialize session
    this.initializeSession();
    
    // Track page navigation
    this.trackNavigation();
    
    // Track user clicks
    this.trackClicks();
  }
  
  private initializeSession() {
    this.context.sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.context.url = window.location.href;
    this.context.userAgent = navigator.userAgent;
    this.context.timestamp = new Date().toISOString();
    
    // Add initial breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'navigation',
      category: 'session',
      message: 'Session started',
      level: 'info',
      data: { url: window.location.href }
    });
  }
  
  updateContext(updates: Partial<ErrorContext>) {
    this.context = { ...this.context, ...updates };
  }
  
  addBreadcrumb(breadcrumb: Breadcrumb) {
    this.breadcrumbs.push(breadcrumb);
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }
  }
  
  addJourneyEvent(event: JourneyEvent) {
    this.journeyEvents.push(event);
    if (this.journeyEvents.length > this.maxJourneyEvents) {
      this.journeyEvents.shift();
    }
  }
  
  private handleError(event: ErrorEvent) {
    const error = event.error || new Error(event.message);
    
    // Add error breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'error',
      message: error.message,
      level: 'error',
      data: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
    
    // Log the error
    this.logError(error, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  }
  
  private handleRejection(event: PromiseRejectionEvent) {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    // Add rejection breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'promise',
      message: `Unhandled promise rejection: ${error.message}`,
      level: 'error',
      data: { reason: event.reason }
    });
    
    // Log the error
    this.logError(error, {
      source: 'unhandledrejection',
      promise: true
    });
  }
  
  private wrapConsole() {
    // Wrap console.error
    console.error = (...args: any[]) => {
      this.originalConsoleError.apply(console, args);
      
      // Create breadcrumb
      this.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'console',
        category: 'console.error',
        message: args.map(arg => String(arg)).join(' '),
        level: 'error',
        data: { arguments: args }
      });
      
      // If first argument is an Error object, log it
      if (args[0] instanceof Error) {
        this.logError(args[0], {
          source: 'console.error',
          consoleArgs: args.slice(1)
        });
      }
    };
    
    // Wrap console.warn
    console.warn = (...args: any[]) => {
      this.originalConsoleWarn.apply(console, args);
      
      // Create breadcrumb
      this.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'console',
        category: 'console.warn',
        message: args.map(arg => String(arg)).join(' '),
        level: 'warning',
        data: { arguments: args }
      });
    };
  }
  
  private trackNavigation() {
    // Track initial page view
    this.trackPageView(window.location.pathname);
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      this.trackPageView(window.location.pathname);
    });
    
    // Override pushState and replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      originalPushState.apply(history, args);
      this.trackPageView(window.location.pathname);
    };
    
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      originalReplaceState.apply(history, args);
      this.trackPageView(window.location.pathname);
    };
  }
  
  trackPageView(path: string, metadata?: any) {
    this.errorLogger.incrementPageViews();
    
    // Add breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'navigation',
      category: 'page_view',
      message: `Navigated to ${path}`,
      level: 'info',
      data: { path, ...metadata }
    });
    
    // Add journey event
    this.addJourneyEvent({
      type: 'page_view',
      page: path,
      metadata,
      timestamp: Date.now()
    });
  }
  
  private trackClicks() {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      // Skip if target is not an interactive element
      if (!this.isInteractiveElement(target)) {
        return;
      }
      
      this.errorLogger.incrementActions();
      
      const elementInfo = this.getElementInfo(target);
      
      // Add breadcrumb
      this.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'click',
        category: 'user_action',
        message: `Clicked ${elementInfo.type}: ${elementInfo.text}`,
        level: 'info',
        data: elementInfo
      });
      
      // Add journey event
      this.addJourneyEvent({
        type: 'user_action',
        action: 'click',
        target: elementInfo.selector,
        metadata: elementInfo,
        timestamp: Date.now()
      });
    }, true);
  }
  
  private isInteractiveElement(element: HTMLElement): boolean {
    const interactiveTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
    return interactiveTags.includes(element.tagName) || 
           element.getAttribute('role') === 'button' ||
           element.hasAttribute('onclick') ||
           element.classList.contains('clickable') ||
           element.classList.contains('btn');
  }
  
  private getElementInfo(element: HTMLElement) {
    return {
      type: element.tagName.toLowerCase(),
      text: element.textContent?.trim().substring(0, 50) || '',
      id: element.id || undefined,
      className: element.className || undefined,
      selector: this.generateSelector(element),
      href: (element as HTMLAnchorElement).href || undefined,
      value: (element as HTMLInputElement).value || undefined
    };
  }
  
  private generateSelector(element: HTMLElement): string {
    if (element.id) {
      return `#${element.id}`;
    }
    
    const classes = element.className
      .split(' ')
      .filter(c => c.trim())
      .map(c => `.${c}`)
      .join('');
    
    return `${element.tagName.toLowerCase()}${classes}`;
  }
  
  trackAction(action: string, target: string, metadata?: any) {
    this.errorLogger.incrementActions();
    
    // Add breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'user_action',
      message: `${action}: ${target}`,
      level: 'info',
      data: metadata
    });
    
    // Add journey event
    this.addJourneyEvent({
      type: 'user_action',
      action,
      target,
      metadata,
      timestamp: Date.now()
    });
  }
  
  trackApiCall(endpoint: string, method: string, status?: number, error?: any) {
    // Add breadcrumb
    this.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'api',
      category: 'api_call',
      message: `${method} ${endpoint} - ${status || 'pending'}`,
      level: error ? 'error' : 'info',
      data: { endpoint, method, status, error }
    });
    
    // Add journey event
    this.addJourneyEvent({
      type: 'api_call',
      endpoint,
      method,
      status,
      metadata: error,
      timestamp: Date.now()
    });
  }
  
  async logError(error: Error | any, additionalContext?: any) {
    const errorContext: Partial<ErrorContext> = {
      ...this.context,
      breadcrumbs: [...this.breadcrumbs],
      timestamp: new Date().toISOString()
    };
    
    const classification = classifyError(error);
    
    // Get last 20 journey events for context
    const lastActions = this.journeyEvents.slice(-20);
    
    await this.errorLogger.logError(
      error,
      classification,
      errorContext,
      {
        ...additionalContext,
        lastActions
      }
    );
  }
  
  clearBreadcrumbs() {
    this.breadcrumbs = [];
  }
  
  clearJourneyEvents() {
    this.journeyEvents = [];
  }
  
  // Public getters for status checking
  get initialized(): boolean {
    return this.isInitialized;
  }
  
  get breadcrumbsCount(): number {
    return this.breadcrumbs.length;
  }
  
  get journeyEventsCount(): number {
    return this.journeyEvents.length;
  }
  
  get currentContext(): Partial<ErrorContext> {
    return { ...this.context };
  }
}

// Export singleton instance
export const errorTracker = GlobalErrorTracker.getInstance();

// Export convenience functions
export function initializeErrorTracking() {
  errorTracker.initialize();
}

export function updateErrorContext(updates: Partial<ErrorContext>) {
  errorTracker.updateContext(updates);
}

export function trackPageView(path: string, metadata?: any) {
  errorTracker.trackPageView(path, metadata);
}

export function trackAction(action: string, target: string, metadata?: any) {
  errorTracker.trackAction(action, target, metadata);
}

export function trackApiCall(endpoint: string, method: string, status?: number, error?: any) {
  errorTracker.trackApiCall(endpoint, method, status, error);
}

export function logError(error: Error | any, context?: any) {
  return errorTracker.logError(error, context);
}

// Check if error tracking is initialized and active
export function isErrorTrackingActive(): boolean {
  return errorTracker.initialized;
}

// Get error tracking status
export function getErrorTrackingStatus() {
  return {
    initialized: errorTracker.initialized,
    breadcrumbCount: errorTracker.breadcrumbsCount,
    journeyEventCount: errorTracker.journeyEventsCount,
    hasGlobalHandlers: typeof window !== 'undefined' && 
      window.addEventListener && 
      errorTracker.initialized,
    context: errorTracker.currentContext
  };
}